<template>
  <div class="sovi-contact-page sovi-home">
    <router-link to="/" class="sovi-contact-page__logo">
      <sovi-logo :size="48" />
    </router-link>

    <v-menu offset-y>
      <template #activator="{ props }">
        <v-btn
          class="language-menu"
          size="default"
          color="#eca1c2"
          v-bind="props"
        >
          <template #prepend>
            <v-icon icon="mdi-web" />
          </template>

          {{ $i18n.locale.toUpperCase() }}
        </v-btn>
      </template>

      <v-list>
        <v-list-item
          v-for="option in $i18n.availableLocales"
          :key="option"
          class="align-items-center"
          @click="onLocaleChange(option)"
        >
          <v-row align="center" no-gutters>
            {{ option.toUpperCase() }}
          </v-row>
        </v-list-item>
      </v-list>
    </v-menu>

    <sovi-content-nav />

    <div class="sovi-contact-page__body">
      <sovi-card :title="t('title')" class="mx-auto max-w-[600px]">
        <p>{{ t('intro') }}</p>

        <v-form
          ref="formRef"
          v-model="valid"
          validate-on="submit lazy"
          @submit.prevent="onSubmit()"
        >
          <v-text-field
            v-model="email"
            :label="t('emailLabel')"
            :rules="rules.email"
            variant="outlined"
            rounded="lg"
            color="tertiary"
            type="email"
            name="email"
            class="my-4"
            autocomplete="email"
            prepend-inner-icon="mdi-email"
          />

          <v-text-field
            v-model="phone"
            :label="t('phoneLabel')"
            :rules="rules.phone"
            variant="outlined"
            class="mb-4"
            rounded="lg"
            color="tertiary"
            type="tel"
            name="phone"
            autocomplete="tel"
            prepend-inner-icon="mdi-phone"
          />

          <v-textarea
            v-model="message"
            :label="t('messageLabel')"
            :rules="rules.message"
            class="sovi-contact-textarea mb-4"
            variant="outlined"
            :rounded="false"
            color="tertiary"
            name="message"
            counter="5000"
            rows="5"
            auto-grow
            prepend-inner-icon="mdi-message-text"
          />

          <v-checkbox v-model="consent" :rules="rules.consent" color="tertiary">
            <template #label>
              <div>
                <p>
                  {{ t('consentText') }}
                  <a
                    class="font-semibold text-tertiary-500 decoration-white underline-offset-4 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="/pdf/Sovi_tietosuojaseloste.pdf"
                    @click.stop
                    v-text="t('consentLink')"
                  />
                </p>
              </div>
            </template>
          </v-checkbox>

          <v-btn
            color="primary"
            type="submit"
            :disabled="!consent || loading"
            :loading="loading"
            block
            size="x-large"
          >
            {{ t('submit') }}
          </v-btn>
        </v-form>
      </sovi-card>
    </div>

    <sovi-status-dialog
      v-model="showSuccessDialog"
      :title="t('successTitle')"
      :subtitle="t('successBody')"
      type="success"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import type { VForm } from 'vuetify/components';
import { definePage } from 'vue-router/auto';
import { useRouteMeta } from '@/composables/useRouteMeta';
import { loadRecaptcha } from '@/plugins/recaptcha';
import { useNotificationStore } from '@/stores/notification';
import { contactRepository } from '@/repositories/contactRepository';
import {
  isValidEmail,
  isValidMessage,
  isValidPhone,
} from '@/pages/kontakti.validation';

const RECAPTCHA_SITE_KEY = '6LdrvAspAAAAALZn5Cqp3qCMDvrIfvNY3A_Y8x__';

definePage({
  meta: {
    hero: false,
    restricted: false,
    maxContainer: true,
  },
});

useRouteMeta({
  title: 'Ota yhteyttä | Sovi.fi',
  description:
    'Ota yhteyttä Sovi.fi-tiimiin. Lähetä viestisi lomakkeella, niin vastaamme sinulle mahdollisimman pian.',
});

const { locale } = useI18n({ useScope: 'global' });
const { t } = useI18n();
const theme = useTheme();
const { addNotification } = useNotificationStore();

const email = ref('');
const phone = ref('');
const message = ref('');
const consent = ref(false);
const loading = ref(false);
const valid = ref(false);
const showSuccessDialog = ref(false);
const formRef = ref<InstanceType<typeof VForm> | null>(null);

const rules = computed(() => ({
  email: [(v: string) => isValidEmail(v) || t('emailInvalid')],
  phone: [(v: string) => isValidPhone(v) || t('phoneInvalid')],
  message: [(v: string) => isValidMessage(v) || t('messageInvalid')],
  consent: [(v: boolean) => v === true || t('consentRequired')],
}));

const onLocaleChange = (newLocale: string) => {
  locale.value = newLocale;
  localStorage.setItem('sovi-locale', newLocale);
};

const onSubmit = async () => {
  if (!formRef.value) return;

  const { valid: ok } = await formRef.value.validate();

  // Per-field validation messages are shown by v-form; no request is sent
  // while the form is invalid.
  if (!ok) return;

  try {
    loading.value = true;

    const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
      action: 'submit',
    });

    const { success } = await contactRepository.sendMessage({
      email: email.value,
      phone: phone.value,
      message: message.value,
      token,
    });

    if (success) {
      // Confirmation modal + fully reset the form (values and validation
      // state) so no stale error styling remains.
      showSuccessDialog.value = true;

      formRef.value.reset();
      formRef.value.resetValidation();
      consent.value = false;
    } else {
      // Error result → error feedback, retain field values.
      addNotification({
        title: t('errorTitle'),
        body: t('errorBody'),
        color: 'error',
        icon: 'mdi-alert',
      });
    }
  } catch {
    // Thrown error / 30s timeout → error feedback, retain values.
    addNotification({
      title: t('errorTitle'),
      body: t('errorBody'),
      color: 'error',
      icon: 'mdi-alert',
    });
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  document.getElementById('sovi-main')?.scrollTo(0, 0);
  window.scrollTo(0, 0);
  loadRecaptcha();

  const storedLocale = localStorage.getItem('sovi-locale');

  if (storedLocale && storedLocale !== locale.value) {
    locale.value = storedLocale;
  }

  theme.global.name.value = 'light';
});
</script>

<style lang="scss">
.grecaptcha-badge {
  visibility: hidden;
}

.sovi-contact-page {
  position: relative;
  background-color: #fff;
  min-height: 100vh;

  &__logo {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 2;
  }

  .language-menu {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    box-shadow:
      rgba(0, 0, 0, 0.15) 0px 15px 25px,
      rgba(0, 0, 0, 0.05) 0px 5px 10px;
  }

  &__body {
    padding: clamp(96px, 12vw, 140px) clamp(16px, 5vw, 32px)
      clamp(32px, 5vw, 64px);
  }
}

// Keep the message textarea a clean rounded rectangle. Vuetify's `rounded`
// prop applies a large fixed radius that reads as a pill on a tall field, so
// we set a small proportional radius on the outlined field instead.
.sovi-contact-textarea .v-field {
  border-radius: 12px;
}
</style>

<i18n lang="json">
{
  "fi": {
    "title": "Ota yhteyttä",
    "intro": "Jätä viestisi oheisella lomakkeella, niin otamme sinuun yhteyttä mahdollisimman pian.",
    "emailLabel": "Sähköpostiosoite",
    "emailInvalid": "Anna kelvollinen sähköpostiosoite",
    "phoneLabel": "Puhelinnumero (valinnainen)",
    "phoneInvalid": "Anna kelvollinen puhelinnumero",
    "messageLabel": "Viesti",
    "messageInvalid": "Kirjoita viesti (1–5000 merkkiä)",
    "consentText": "Hyväksyn henkilötietojeni käsittelyn.",
    "consentLink": "Tutustu tietosuojaselosteeseen",
    "consentRequired": "Hyväksy tietosuojaseloste jatkaaksesi",
    "submit": "Lähetä viesti",
    "successTitle": "Viesti lähetetty",
    "successBody": "Vastaamme sinulle 1–7 päivän kuluessa.",
    "errorTitle": "Lähetys epäonnistui",
    "errorBody": "Viestin lähettäminen ei onnistunut. Yritä myöhemmin uudelleen."
  },
  "en": {
    "title": "Contact us",
    "intro": "Leave your message using the form below and we will get back to you as soon as possible.",
    "emailLabel": "Email address",
    "emailInvalid": "Please enter a valid email address",
    "phoneLabel": "Phone number (optional)",
    "phoneInvalid": "Please enter a valid phone number",
    "messageLabel": "Message",
    "messageInvalid": "Please enter a message (1–5000 characters)",
    "consentText": "I agree to the processing of my personal data.",
    "consentLink": "Read the data protection statement",
    "consentRequired": "Please accept the data protection statement to continue",
    "submit": "Send message",
    "successTitle": "Message sent",
    "successBody": "We will reply within 1-7 days.",
    "errorTitle": "Sending failed",
    "errorBody": "The message could not be sent. Please try again later."
  }
}
</i18n>
